import type { Resolver } from "react-hook-form";
import type { ContactFormValues } from "@/app/validations/contactSchema";

// The contact form sits on ~40 pages, usually at the very bottom. Its submit
// stack (axios + js-cookie) and validation stack (yup + resolver) are only
// needed once the visitor actually submits, so they're fetched on first use
// instead of shipping in every page's initial JS. Behavior is identical: the
// same schema and the same request run, just loaded on demand.

let submitModule: Promise<typeof import("@/services/contactService")> | null = null;

export async function submitContactForm(data: FormData) {
  // Don't cache a failed chunk load (e.g. flaky network) — retry next time.
  submitModule ??= import("@/services/contactService").catch((error) => {
    submitModule = null;
    throw error;
  });
  const { default: contactService } = await submitModule;
  return contactService.submitForm(data);
}

type ValidateContactDetails =
  typeof import("@/app/validations/contactDetailsSchema").validateContactDetails;

// Same yup-based validation the popups used before, loaded on first submit.
export async function validateContactDetails(
  ...args: Parameters<ValidateContactDetails>
): Promise<ReturnType<ValidateContactDetails>> {
  const { validateContactDetails: validate } = await import(
    "@/app/validations/contactDetailsSchema"
  );
  return validate(...args);
}

let resolverPromise: Promise<Resolver<ContactFormValues>> | null = null;

export const lazyContactResolver: Resolver<ContactFormValues> = async (
  values,
  context,
  options,
) => {
  resolverPromise ??= Promise.all([
    import("@hookform/resolvers/yup"),
    import("@/app/validations/contactSchema"),
  ])
    .then(([{ yupResolver }, { contactSchema }]) => yupResolver(contactSchema))
    .catch((error) => {
      resolverPromise = null;
      throw error;
    });
  const resolver = await resolverPromise;
  return resolver(values, context, options);
};
